import React, { useEffect, useState } from "react";
import { FileText, Link2, Copy, Clock, RefreshCw, AlertCircle, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Shareable Docs tile — CEO surface to mint short-lived public download
 * URLs for the canonical playbooks, handovers, reconcile CSVs we keep
 * in /app/memory/.
 *
 * Each entry shows: name + description + a "Mint link" button. Clicking
 * the button:
 *   1. Calls POST /api/admin/docs/issue-link/{doc_id}?expires_in_hours=720
 *   2. Receives the signed URL back
 *   3. Auto-copies it to clipboard + shows toast
 *   4. Stores it locally so the next click is "Re-mint" (always fresh)
 *
 * The catalogue itself comes from GET /api/admin/docs/catalogue —
 * single source of truth, no hard-coding of doc IDs here.
 */
export default function ShareableDocsTile() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  // doc_id → { url, expires_at }
  const [recentLinks, setRecentLinks] = useState({});

  const fetchCatalogue = async () => {
    setLoading(true); setErr("");
    try {
      const token = localStorage.getItem("scaleboard_token");
      const r = await fetch(`${API}/api/admin/docs/catalogue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setDocs(j.docs || []);
    } catch (e) {
      setErr(e.message || "Failed to load catalogue");
    } finally { setLoading(false); }
  };

  const mintLink = async (doc) => {
    setBusy(doc.doc_id);
    try {
      const token = localStorage.getItem("scaleboard_token");
      const r = await fetch(
        `${API}/api/admin/docs/issue-link/${doc.doc_id}?expires_in_hours=720`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setRecentLinks((prev) => ({
        ...prev,
        [doc.doc_id]: { url: j.url, expires_at: j.expires_at },
      }));
      // Auto-copy
      try {
        await navigator.clipboard.writeText(j.url);
        toast.success("Link copied to clipboard · 30-day TTL");
      } catch {
        toast.success("Link minted (clipboard access blocked — copy manually)");
      }
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally { setBusy(null); }
  };

  const copyAgain = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Clipboard access blocked");
    }
  };

  useEffect(() => { fetchCatalogue(); }, []);

  const fmtExpiry = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div
      data-testid="shareable-docs-tile"
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-amber-50/30 p-6 shadow-sm"
    >
      <div className="mb-5 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            <h3 className="font-serif text-xl font-semibold text-slate-900">
              Shareable Docs
            </h3>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700">
              CEO · WhatsApp-ready
            </span>
          </div>
          <p className="text-sm italic text-slate-500">
            Mint a fresh short-lived public link for any canonical playbook,
            handover, or reconcile CSV. Auto-copied to clipboard — paste straight
            into WhatsApp / email.
          </p>
        </div>
        <button
          data-testid="shareable-docs-refresh"
          onClick={fetchCatalogue}
          disabled={loading}
          className="rounded-full border border-slate-200 bg-white p-2 transition hover:bg-slate-50 disabled:opacity-40"
          title="Refresh catalogue"
        >
          <RefreshCw className={`h-4 w-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {err ? (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{err}</span>
        </div>
      ) : null}

      {loading && !docs.length ? (
        <div className="py-8 text-center text-sm text-slate-400">
          Loading catalogue…
        </div>
      ) : null}

      <div className="space-y-3">
        {docs.map((d) => {
          const recent = recentLinks[d.doc_id];
          const isBusy = busy === d.doc_id;
          const isMissing = !d.exists;
          return (
            <div
              key={d.doc_id}
              data-testid={`shareable-doc-${d.doc_id}`}
              className={`rounded-xl border bg-white p-4 transition ${
                isMissing
                  ? "border-rose-200 bg-rose-50/30 opacity-60"
                  : "border-slate-200 hover:border-amber-300"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="truncate font-mono text-[11px] font-medium uppercase tracking-wider text-slate-400"
                      title={d.doc_id}
                    >
                      {d.doc_id}
                    </span>
                    {isMissing ? (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
                        Missing on disk
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 font-medium text-slate-900">
                    {d.filename}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {d.description}
                  </p>
                </div>
                <button
                  data-testid={`shareable-doc-mint-${d.doc_id}`}
                  onClick={() => mintLink(d)}
                  disabled={isBusy || isMissing}
                  className={`flex-shrink-0 inline-flex items-center rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider transition ${
                    isBusy
                      ? "bg-amber-100 text-amber-700"
                      : isMissing
                      ? "cursor-not-allowed bg-slate-100 text-slate-400"
                      : recent
                      ? "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {isBusy ? (
                    <><RefreshCw className="mr-1 h-3 w-3 animate-spin" />Minting…</>
                  ) : recent ? (
                    <><RefreshCw className="mr-1 h-3 w-3" />Re-mint</>
                  ) : (
                    <><Link2 className="mr-1 h-3 w-3" />Mint link</>
                  )}
                </button>
              </div>

              {recent ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/50 p-2.5">
                  <div className="mb-1 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-amber-700">
                    <span className="flex items-center">
                      <Check className="mr-1 h-3 w-3" />
                      Live link · expires {fmtExpiry(recent.expires_at)}
                    </span>
                    <button
                      data-testid={`shareable-doc-copy-${d.doc_id}`}
                      onClick={() => copyAgain(recent.url)}
                      className="inline-flex items-center text-amber-700 hover:text-amber-900"
                    >
                      <Copy className="mr-1 h-3 w-3" />
                      Copy again
                    </button>
                  </div>
                  <code
                    className="block break-all rounded bg-white px-2 py-1.5 font-mono text-[10px] text-slate-700"
                    onClick={() => copyAgain(recent.url)}
                    title="Click to copy"
                  >
                    {recent.url}
                  </code>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <span>
          <Clock className="mr-1 inline h-3 w-3" />
          Links expire 30 days after minting · audit log in `public_docs_tokens`
        </span>
        <span className="font-medium">{docs.length} docs in catalogue</span>
      </div>
    </div>
  );
}
